import { forwardRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  formatIncompletePhoneNumber,
  getCountryCallingCode,
} from "libphonenumber-js/min";
import { noop, findBy } from "neetocist";
import { Input } from "neetoui";
import PropTypes from "prop-types";

import { IP_LOOKUP_STORAGE_KEY } from "./constants";
import CountryPicker from "./CountryPicker";
import { COUNTRY_PICKER_OPTIONS } from "./CountryPicker/constants";
import {
  getPhoneData,
  isNumeric,
  preserveCursor,
  fetchLocationDetails,
} from "./utils";

const PhoneNumberInput = forwardRef(
  (
    {
      value = "",
      onChange = noop,
      className,
      error,
      selectProps,
      initialCountryCode,
      ...inputProps
    },
    ref
  ) => {
    const [countryCode, setCountryCode] = useState(
      () => getPhoneData(value).countryCode || initialCountryCode
    );

    const { data: ipCountry } = useQuery({
      queryKey: [IP_LOOKUP_STORAGE_KEY],
      queryFn: fetchLocationDetails,
      enabled: !countryCode,
      staleTime: Infinity,
      gcTime: Infinity,
      select: data =>
        findBy({ value: data.countryCode }, COUNTRY_PICKER_OPTIONS),
    });

    const finalCountryCode = countryCode || ipCountry?.value || "US";
    const formattedValue = formatIncompletePhoneNumber(value, finalCountryCode);

    const onKeyDown = preserveCursor(event => {
      // Handles special characters in the phone number formatting like brackets and spaces.
      if (event.key !== "Backspace") return;
      const left = event.target.value.substring(0, event.target.selectionStart);
      const right = event.target.value.substring(event.target.selectionEnd);
      let index = left.length - 1;
      while (index > 0 && !isNumeric(left[index])) index--;
      const newValue = left.substring(0, index + 1) + right;
      if (newValue) event.target.value = newValue;
    });

    const onInputChange = preserveCursor(event => {
      const phoneData = getPhoneData(event.target.value, finalCountryCode);
      event.target.value = formatIncompletePhoneNumber(event.target.value);
      setCountryCode(phoneData.countryCode);
      onChange(phoneData.phoneNumber);
    });

    const onChangeCountry = countryCode => {
      const phoneData = getPhoneData(value, finalCountryCode);
      const prevPrefix = `+${getCountryCallingCode(phoneData.countryCode)}`;
      const nextPrefix = `+${getCountryCallingCode(countryCode)}`;
      setCountryCode(countryCode);
      onChange(nextPrefix + value.replace(prevPrefix, ""));
    };

    return (
      <div {...{ className }}>
        <Input
          {...{ error, onKeyDown, ref }}
          autoComplete="tel"
          className="neeto-molecules-phone-number-input__input"
          data-testid="phonenumber-input"
          inputMode="tel"
          type="tel"
          value={formattedValue}
          prefix={
            <CountryPicker
              countryCode={finalCountryCode}
              onChange={onChangeCountry}
              {...selectProps}
            />
          }
          onChange={onInputChange}
          {...inputProps}
        />
      </div>
    );
  }
);

PhoneNumberInput.displayName = "PhoneNumberInput";

PhoneNumberInput.propTypes = {
  /**
   * To specify the text to be displayed in the component.
   */
  value: PropTypes.string,
  /**
   * To specify the function to be triggered when the `value` is updated.
   */
  onChange: PropTypes.func,
  /**
   * To specify external classNames that can be provided as overrides to the main wrapper.
   */
  className: PropTypes.string,
  /**
   * To specify the error message to be displayed below the input field.
   */
  error: PropTypes.string,
  /**
   * To specify the additional props to be provided to the country picker `Select` component.
   */
  selectProps: PropTypes.object,
  /**
   * To specify the initial country to be set when component is mounted. Setting this
   * disables the feature of setting the initial country based on user's IP address.
   */
  initialCountryCode: PropTypes.string,
};

export default PhoneNumberInput;
